import { useCallback, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';

import { cn } from '../base/utils';
import { BaseButton } from '../base/BaseButton';

export interface WorkbenchLaneColumn<ColumnId extends string = string> {
  id: ColumnId;
  label?: ReactNode;
  icon?: ReactNode;
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
  badgeClassName?: string;
  iconClassName?: string;
}

export interface WorkbenchLaneClassNames {
  root?: string;
  column?: string;
  columnHeader?: string;
  columnIcon?: string;
  columnBadge?: string;
  columnCount?: string;
  columnBody?: string;
  cardWrapper?: string;
  overlayWrapper?: string;
  emptyColumn?: string;
}

export interface WorkbenchLaneRenderCardContext<Row = any, ColumnId extends string = string> {
  row: Row;
  rowIndex: number;
  column: WorkbenchLaneColumn<ColumnId>;
  isDragging: boolean;
  isOverlay: boolean;
}

export interface WorkbenchLaneViewProps<Row = any, ColumnId extends string = string> {
  rows?: Row[];
  columns?: Array<WorkbenchLaneColumn<ColumnId>>;
  getRowId?: (row: Row, rowIndex: number) => string;
  getColumnId?: (row: Row, rowIndex: number) => ColumnId;
  renderCard?: (row: Row, context: WorkbenchLaneRenderCardContext<Row, ColumnId>) => ReactNode;
  renderOverlayCard?: (row: Row, context: WorkbenchLaneRenderCardContext<Row, ColumnId>) => ReactNode;
  onMoveRow?: (rowId: string, nextColumnId: ColumnId, row: Row) => void;
  onSelectRow?: (intent: { rowId: string }) => void;
  emptyColumn?: ReactNode;
  activationDistance?: number;
  columnWidth?: number | string;
  columnMinHeight?: number | string;
  className?: string;
  classNames?: WorkbenchLaneClassNames;
  style?: CSSProperties;
}

function toCssSize(value: number | string | undefined) {
  if (value === undefined) return undefined;
  return typeof value === 'number' ? `${value}px` : value;
}

function defaultGetRowId(row: any, rowIndex: number) {
  return String(row?.id ?? row?.key ?? rowIndex);
}

function defaultGetColumnId<ColumnId extends string = string>(row: any) {
  return String(row?.columnId ?? row?.status ?? '') as ColumnId;
}

function defaultRenderCard<Row>(row: Row) {
  const value = (row as any)?.title ?? (row as any)?.name ?? (row as any)?.id ?? 'Untitled';
  return (
    <div className="rounded-lg border border-border/70 bg-card px-3 py-2 text-sm text-foreground shadow-sm">
      {String(value)}
    </div>
  );
}

interface WorkbenchLaneDraggableCardProps<Row, ColumnId extends string> {
  row: Row;
  rowId: string;
  rowIndex: number;
  column: WorkbenchLaneColumn<ColumnId>;
  classNames?: WorkbenchLaneClassNames;
  renderCard: (row: Row, context: WorkbenchLaneRenderCardContext<Row, ColumnId>) => ReactNode;
}

function WorkbenchLaneDraggableCard<Row, ColumnId extends string>({
  row,
  rowId,
  rowIndex,
  column,
  classNames,
  renderCard,
}: WorkbenchLaneDraggableCardProps<Row, ColumnId>) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: rowId });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={cn('touch-none', classNames?.cardWrapper)}
    >
      {renderCard(row, { row, rowIndex, column, isDragging, isOverlay: false })}
    </div>
  );
}

interface WorkbenchLaneColumnViewProps<Row, ColumnId extends string> {
  column: WorkbenchLaneColumn<ColumnId>;
  columnRows: Array<{ row: Row; rowIndex: number; rowId: string }>;
  columnWidth?: number | string;
  columnMinHeight?: number | string;
  emptyColumn?: ReactNode;
  classNames?: WorkbenchLaneClassNames;
  renderCard: (row: Row, context: WorkbenchLaneRenderCardContext<Row, ColumnId>) => ReactNode;
}

function WorkbenchLaneColumnView<Row, ColumnId extends string>({
  column,
  columnRows,
  columnWidth,
  columnMinHeight,
  emptyColumn,
  classNames,
  renderCard,
}: WorkbenchLaneColumnViewProps<Row, ColumnId>) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });

  return (
    <section
      className={cn(
        'flex shrink-0 flex-col rounded-xl border border-border/60 bg-card p-2 shadow-sm',
        classNames?.column,
        column.className,
      )}
      style={{ width: toCssSize(columnWidth) ?? '280px' }}
    >
      <div className={cn('flex h-10 items-center gap-2 px-1', classNames?.columnHeader, column.headerClassName)}>
        {column.icon ? (
          <span className={cn('inline-flex h-4 w-4 items-center justify-center text-muted-foreground', classNames?.columnIcon, column.iconClassName)}>
            {column.icon}
          </span>
        ) : null}
        <span className={cn('inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold bg-muted text-muted-foreground', classNames?.columnBadge, column.badgeClassName)}>
          {column.label ?? column.id}
        </span>
        <span className={cn('text-xs text-muted-foreground', classNames?.columnCount)}>{columnRows.length}</span>
      </div>
      <div
        ref={setNodeRef}
        className={cn(
          'mt-1 flex min-h-[200px] flex-1 flex-col gap-2 overflow-y-auto rounded-lg p-1 transition-colors [scrollbar-width:thin]',
          isOver && 'bg-accent/60',
          classNames?.columnBody,
          column.bodyClassName,
        )}
        style={{ minHeight: toCssSize(columnMinHeight) }}
      >
        {columnRows.length ? columnRows.map(({ row, rowIndex, rowId }) => (
          <WorkbenchLaneDraggableCard
            key={rowId}
            row={row}
            rowId={rowId}
            rowIndex={rowIndex}
            column={column}
            classNames={classNames}
            renderCard={renderCard}
          />
        )) : emptyColumn ? (
          <div className={cn('flex min-h-24 items-center justify-center rounded-lg border border-dashed border-border/60 px-3 py-4 text-xs text-muted-foreground', classNames?.emptyColumn)}>
            {emptyColumn}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export function WorkbenchLaneView<Row = any, ColumnId extends string = string>({
  rows = [],
  columns = [],
  getRowId = defaultGetRowId,
  getColumnId = defaultGetColumnId<ColumnId>,
  renderCard,
  renderOverlayCard,
  onMoveRow,
  onSelectRow,
  emptyColumn,
  activationDistance = 5,
  columnWidth = 280,
  columnMinHeight,
  className,
  classNames,
  style,
}: WorkbenchLaneViewProps<Row, ColumnId>) {
  const cardRenderer = renderCard ?? ((row: Row, context: WorkbenchLaneRenderCardContext<Row, ColumnId>) => onSelectRow
    ? <BaseButton className="w-full justify-start whitespace-normal text-left" onClick={() => onSelectRow({ rowId: getRowId(row, context.rowIndex) })}>{String((row as any)?.title ?? (row as any)?.name ?? getRowId(row, context.rowIndex))}</BaseButton>
    : defaultRenderCard(row));
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: activationDistance } }));
  const columnMap = useMemo(() => new Map(columns.map((column) => [column.id, column])), [columns]);
  const rowEntries = useMemo(() => rows.map((row, rowIndex) => ({
    row,
    rowIndex,
    rowId: getRowId(row, rowIndex),
    columnId: getColumnId(row, rowIndex),
  })), [getColumnId, getRowId, rows]);
  const activeEntry = activeRowId ? rowEntries.find((entry) => entry.rowId === activeRowId) : undefined;
  const rowsByColumn = useMemo(() => {
    const next = new Map<ColumnId, Array<{ row: Row; rowIndex: number; rowId: string }>>();
    columns.forEach((column) => next.set(column.id, []));
    rowEntries.forEach(({ row, rowIndex, rowId, columnId }) => {
      next.get(columnId)?.push({ row, rowIndex, rowId });
    });
    return next;
  }, [columns, rowEntries]);
  const activeColumn = activeEntry ? columnMap.get(activeEntry.columnId) : undefined;

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveRowId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveRowId(null);
    const rowId = String(event.active.id);
    const nextColumnId = String(event.over?.id ?? '') as ColumnId;
    const activeRow = rowEntries.find((entry) => entry.rowId === rowId);
    if (!activeRow || !columnMap.has(nextColumnId) || activeRow.columnId === nextColumnId) return;
    onMoveRow?.(rowId, nextColumnId, activeRow.row);
  }, [columnMap, onMoveRow, rowEntries]);

  const handleDragCancel = useCallback(() => {
    setActiveRowId(null);
  }, []);

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
      <div
        className={cn('flex min-h-0 flex-1 gap-4 overflow-x-auto p-4 [scrollbar-width:thin]', classNames?.root, className)}
        style={style}
      >
        {columns.map((column) => (
          <WorkbenchLaneColumnView
            key={column.id}
            column={column}
            columnRows={rowsByColumn.get(column.id) || []}
            columnWidth={columnWidth}
            columnMinHeight={columnMinHeight}
            emptyColumn={emptyColumn}
            classNames={classNames}
            renderCard={cardRenderer}
          />
        ))}
      </div>
      <DragOverlay>
        {activeEntry && activeColumn ? (
          <div className={cn(classNames?.overlayWrapper)}>
            {(renderOverlayCard || cardRenderer)(activeEntry.row, {
              row: activeEntry.row,
              rowIndex: activeEntry.rowIndex,
              column: activeColumn,
              isDragging: false,
              isOverlay: true,
            })}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
